import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { API_ACCOUNT_CLIENT, VIEW_DEVELOPER_AGREEMENT } from '../../common/EsConstants';
import { useEsClipboard } from '../../common/EsClipboard';
import { esDayMonYear } from '../../common/EsDate';
import { esDelete, esPost, esRead } from '../../common/EsFetch';
import { EsCheckbox } from '../../common/EsPrime';
import { EsEmptyState } from '../../common/EsEmptyState';
import { useEsStrings } from '../../common/EsStrings';

// esback's EsGameClientService.PLATFORMS, in its order (es-unreal-plugin.md §8).
export const PLATFORMS = [ 'WINDOWS', 'MAC', 'LINUX', 'STEAM_FRAME', 'QUEST', 'ANDROID_XR', 'VISION_PRO', 'XBOX',
    'PLAYSTATION', 'PSVR2', 'SWITCH' ];

/** A platform as a reader names it. */
const platformText = ( pPlatform, t ) => ( {
    WINDOWS: t( 'account-clients-platform-windows', 'Windows' ),
    MAC: t( 'account-clients-platform-mac', 'Mac' ),
    LINUX: t( 'account-clients-platform-linux', 'Linux' ),
    STEAM_FRAME: t( 'account-clients-platform-steam-frame', 'Steam Frame' ),
    QUEST: t( 'account-clients-platform-quest', 'Quest' ),
    ANDROID_XR: t( 'account-clients-platform-android-xr', 'Android XR' ),
    VISION_PRO: t( 'account-clients-platform-vision-pro', 'Apple Vision Pro' ),
    XBOX: t( 'account-clients-platform-xbox', 'Xbox' ),
    PLAYSTATION: t( 'account-clients-platform-playstation', 'PlayStation' ),
    PSVR2: t( 'account-clients-platform-psvr2', 'PlayStation VR2' ),
    SWITCH: t( 'account-clients-platform-switch', 'Switch' ),
}[ pPlatform ] ?? pPlatform );

const testIdOf = ( pPlatform ) => pPlatform.toLowerCase().replaceAll( /_/g, '-' );

const inputClass = 'w-full border border-field-edge bg-field rounded-md shadow-one'
    + ' dark:shadow-register py-3 px-6 text-body text-base placeholder-placeholder outline-hidden'
    + ' focus-visible:shadow-none focus:border-focus';

/* The account page's Game clients tab (es-unreal-plugin.md §6.1): the games this account has
   registered for the Unreal plugin, a form to register one, and a revoke for each that signs out
   every device signed in through it. The id is what a developer types into the plugin's
   settings; it ships inside the game, so it is public and there is no secret to show. Drawn
   while rolloutEsUnrealPlugin is on. */
export default function EsAccountClients() {
    const t = useEsStrings();
    const { copied, copy } = useEsClipboard();
    const [ clients, setClients ] = useState( null );
    const [ name, setName ] = useState( '' );
    const [ platforms, setPlatforms ] = useState( [] );
    const [ confirming, setConfirming ] = useState( null );
    const [ copiedId, setCopiedId ] = useState( null );
    const [ status, setStatus ] = useState( null );
    const [ error, setError ] = useState( null );
    const [ busy, setBusy ] = useState( false );

    const failed = ( aFailure ) => setError( aFailure?.message ?? t( 'account-clients-error', 'That did not work. Try again.' ) );

    const load = () => esRead( API_ACCOUNT_CLIENT )
        .then( ( pList ) => setClients( pList ?? [] ) )
        .catch( ( aFailure ) => {
            setClients( [] );
            failed( aFailure );
        } );

    // Once, when the tab opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect( () => { load(); }, [] );

    const toggle = ( pPlatform, pOn ) => setPlatforms( ( pWas ) =>
        pOn ? [ ...pWas.filter( ( p ) => p !== pPlatform ), pPlatform ] : pWas.filter( ( p ) => p !== pPlatform ) );

    const run = async ( pWork ) => {
        setBusy( true );
        setError( null );
        setStatus( null );
        try {
            await pWork();
            await load();
        }
        catch ( aFailure ) {
            failed( aFailure );
        }
        finally {
            setBusy( false );
        }
    };

    const register = ( pEvent ) => {
        pEvent.preventDefault();
        return run( async () => {
            const aMade = await esPost( API_ACCOUNT_CLIENT, { name, platforms } );
            setStatus( t( 'account-clients-registered', '{name} is registered.', { name: aMade.name } ) );
            setName( '' );
            setPlatforms( [] );
        } );
    };

    const revoke = ( pClient ) => run( async () => {
        await esDelete( API_ACCOUNT_CLIENT + '/' + pClient.id );
        setStatus( t( 'account-clients-revoked', '{name} is revoked. Its devices are signed out.', { name: pClient.name } ) );
        setConfirming( null );
    } );

    const copyId = async ( pClient ) => {
        if ( await copy( String( pClient.id ) ) ) setCopiedId( pClient.id );
    };

    if ( clients === null ) return null;

    return (
        <div data-testid="es-account-clients">
            <p className="mb-6 text-sm text-body">
                { t( 'account-clients-blurb', 'Games you have registered for the Escapade Unreal plugin. Put a game\'s id in the plugin\'s settings. Revoke a game and every device signed in through it is signed out.' ) }
                { ' ' }
                <Link to={ VIEW_DEVELOPER_AGREEMENT } data-testid="es-account-clients-agreement-link" className="whitespace-nowrap underline">
                    { t( 'account-clients-agreement', 'Developer agreement' ) }
                </Link>
            </p>

            <form data-testid="es-account-clients-form" onSubmit={ register } className="mb-8">
                <div className="mb-4">
                    <label className="mb-3 block text-sm font-medium text-heading" htmlFor="clientName">
                        { t( 'account-clients-name-label', 'Game name' ) }
                    </label>
                    <input id="clientName" name="clientName" data-testid="es-account-clients-name-input"
                           value={ name } maxLength={ 64 } autoComplete="off"
                           onChange={ ( pEvent ) => setName( pEvent.target.value ) } className={ inputClass } />
                </div>
                <fieldset className="mb-6">
                    <legend className="mb-3 block text-sm font-medium text-heading">
                        { t( 'account-clients-platforms-label', 'Platforms' ) }
                    </legend>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                        { PLATFORMS.map( ( pPlatform ) => (
                            <label key={ pPlatform } htmlFor={ 'clientPlatform' + pPlatform } className="flex items-center gap-2 text-sm text-body">
                                <EsCheckbox id={ 'clientPlatform' + pPlatform } name={ pPlatform }
                                            checked={ platforms.includes( pPlatform ) }
                                            onChange={ ( pEvent ) => toggle( pPlatform, pEvent.target.value ) }
                                            data-testid={ 'es-account-clients-platform-' + testIdOf( pPlatform ) } />
                                { platformText( pPlatform, t ) }
                            </label>
                        ) ) }
                    </div>
                </fieldset>
                <p data-testid="es-account-clients-accepts" className="mb-4 text-sm text-body">
                    { t( 'account-clients-accepts', 'Registering a game accepts the developer agreement for this account.' ) }
                </p>
                <button type="submit" data-testid="es-account-clients-register-button" disabled={ busy || !name.trim() }
                        className="buttonSubmit flex w-full items-center justify-center disabled:opacity-50">
                    { t( 'account-clients-register', 'Register game' ) }
                </button>
            </form>

            { status &&
                <p data-testid="es-account-clients-status" role="status" className="formStatus mb-4">
                    { status }
                </p> }

            { clients.length === 0
                ? <EsEmptyState icon="pi-link" data-testid="es-account-clients-empty">
                    { t( 'account-clients-empty', 'No games are registered.' ) }
                </EsEmptyState>
                : <ul data-testid="es-account-clients-list" className="divide-y divide-field-edge">
                    { clients.map( ( pClient ) => (
                        <li key={ pClient.id } data-testid={ `es-account-clients-row-${ pClient.id }` }
                            className="flex flex-wrap items-center justify-between gap-3 py-4">
                            <div className="min-w-0">
                                <p data-testid={ `es-account-clients-row-${ pClient.id }-name` } className="font-medium text-heading">
                                    { pClient.name }
                                </p>
                                <p className="text-xs text-aside">
                                    { t( 'account-clients-id', 'Id' ) }
                                    { ' ' }
                                    <code data-testid={ `es-account-clients-row-${ pClient.id }-id` }>{ pClient.id }</code>
                                    { ' ' }
                                    <button type="button" onClick={ () => copyId( pClient ) }
                                            data-testid={ `es-account-clients-row-${ pClient.id }-copy` }
                                            className="rounded-sm px-1 text-xs underline ring-focus focus:ring-2">
                                        { copied && copiedId === pClient.id
                                            ? t( 'account-clients-copied', 'Copied' )
                                            : t( 'account-clients-copy', 'Copy' ) }
                                    </button>
                                </p>
                                { pClient.platforms.length > 0 &&
                                    <p data-testid={ `es-account-clients-row-${ pClient.id }-platforms` } className="text-xs text-aside">
                                        { pClient.platforms.map( ( p ) => platformText( p, t ) ).join( ' · ' ) }
                                    </p> }
                                <p className="text-xs text-aside">
                                    { t( 'account-clients-created', 'Registered {date}', { date: esDayMonYear( pClient.created ) } ) }
                                </p>
                            </div>
                            { confirming === pClient.id
                                ? <div className="flex gap-2">
                                    <button type="button" disabled={ busy } onClick={ () => revoke( pClient ) }
                                            data-testid={ `es-account-clients-row-${ pClient.id }-confirm` }
                                            className="rounded-sm px-2 py-1 text-sm font-bold text-danger ring-focus focus:ring-2 disabled:opacity-50">
                                        { t( 'account-clients-revoke-confirm', 'Revoke it' ) }
                                    </button>
                                    <button type="button" disabled={ busy } onClick={ () => setConfirming( null ) }
                                            data-testid={ `es-account-clients-row-${ pClient.id }-cancel` }
                                            className="rounded-sm px-2 py-1 text-sm text-body ring-focus focus:ring-2 disabled:opacity-50">
                                        { t( 'account-clients-keep', 'Keep it' ) }
                                    </button>
                                </div>
                                : <button type="button" disabled={ busy } onClick={ () => setConfirming( pClient.id ) }
                                          data-testid={ `es-account-clients-row-${ pClient.id }-revoke` }
                                          className="rounded-sm px-2 py-1 text-sm text-body underline ring-focus focus:ring-2 disabled:opacity-50">
                                    { t( 'account-clients-revoke', 'Revoke' ) }
                                </button> }
                        </li>
                    ) ) }
                </ul> }

            { error && <p data-testid="es-account-clients-error" role="alert" className="formError mt-4"> { error } </p> }
        </div>
    );
}
